import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import qs from 'qs';

import { l10n } from '@ott/l10n';
import {
  actions as virtualCardActions,
  selectors as virtualCardSelectors,
} from '@ott/shared-virtual-card-logic';
import KM from '@ott/utility-km';

import { unwrapResult } from '@reduxjs/toolkit';

import ErrorModal from '../ErrorModal';
import IFrameModal from '../IFrameModal';
import SuccessModal from '../SuccessModal';

const { getCardInfo, getTransferLink } = virtualCardActions;

const defaultIFrameModalSize = {
  width: 387,
  height: 636,
};

type Props = {
  isOpen?: boolean;
  kmParams: Record<string, string>;
  onClose: VoidFunction;
};

const TransferModal = ({ isOpen = true, kmParams, onClose }: Props) => {
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const transferLink = useSelector(virtualCardSelectors.getTransferLink);
  const { data } = transferLink;
  const src = data ? data.redirectUrl : undefined;
  const operationId = data ? data.operationId : null;

  const dispatch = useDispatch<any>();

  useEffect(() => {
    dispatch(getTransferLink())
      .then(unwrapResult)
      .catch(() => {
        setIsError(true);
      });
  }, [dispatch]);

  const handleError = useCallback(() => setIsError(true), []);

  const handleErrorModalClose = useCallback(() => {
    setIsError(false);
    onClose();
  }, [onClose]);

  const handleSuccessModalClose = useCallback(() => {
    setIsSuccess(false);
    onClose();
  }, [onClose]);

  const handleSuccess = useCallback(() => {
    setIsSuccess(true);
    dispatch(getCardInfo());
  }, [dispatch]);

  const handleReceiveMessage = useCallback(
    (message: MessageEvent) => {
      const { type, event, search } = message.data;
      if (event === 'redirect' && type === 'result') {
        const { errorCode } = qs.parse(search, { ignoreQueryPrefix: true });

        if (errorCode) {
          KM('Virtual_Card_Transfer_Fail', {
            obj: { ...kmParams, error: errorCode, operation_id: operationId },
          });
          handleError();
        } else {
          KM('Virtual_Card_Transfer_Success', { obj: { ...kmParams, operation_id: operationId } });
          handleSuccess();
        }
      }
    },
    [handleError, handleSuccess, kmParams, operationId]
  );

  if (isError) {
    return (
      <ErrorModal
        isOpen={isOpen}
        onClose={handleErrorModalClose}
        onAction={handleErrorModalClose}
      />
    );
  }

  if (isSuccess) {
    return (
      <SuccessModal
        isOpen={isOpen}
        title={l10n('virtualCard.transferSuccess.title')}
        description={l10n('virtualCard.transferSuccess.description')}
        actionButtonText={l10n('virtualCard.transferSuccess.action')}
        actionButtonPurpose="neutral"
        onAction={handleSuccessModalClose}
        onClose={handleSuccessModalClose}
      />
    );
  }

  return (
    <IFrameModal
      isOpen={isOpen}
      src={src}
      onClose={onClose}
      onMessage={handleReceiveMessage}
      defaultSize={defaultIFrameModalSize}
    />
  );
};

export default React.memo(TransferModal);
